import { BiBookmark, BiLike, BiSolidLike } from "react-icons/bi";
import { GoShareAndroid, GoBookmarkFill } from "react-icons/go";
import { Spin } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useStore from "../../../store/useStore";
import BookmarkToast from "./BookmarkToast";
import html2canvas from "html2canvas";
import usePlanDetails from "../../../hooks/usePlanDetails";
import medleyImage from "../../../assets/images/medley.svg";
import { isZymedApp, shouldShowSponsorship } from "../../../utils/appConfig";
import { sendMessageToParent } from "../../../utils/helper";
import OptimizedImage from "../../../commonComponents/OptimizedImage";
import useImagePreloader from "../../../hooks/useImagePreloader";
import backIcon from "../../../assets/images/arrow-left.svg";
import { ImageCache } from "../../../utils/imageCache";
import { trackZymedEvent, ZYMED_EVENTS } from "../../../utils/zymedAnalytics";


export default function Shot({ data, topic = "", showTabsBar }: any) {
  const navigate = useNavigate();
  const {
    planDetails,
    likedShots,
    likeCounts,
    pendingLikes,
    updateLikeStatus,
    updateBookmarkStatus,
    bookmarkedShots,
    tatvaShots,
  } = useStore();
  const { getPlanDetails } = usePlanDetails();
  const { shot_plan, isSelfShotsPlan } = planDetails || {};
  const { service_payer } = shot_plan || {};
  const shortRef: any = useRef();
  const [showBookmarkToast, setShowBookmarkToast] = useState<number | null>(
    null
  );
  const [snapshotUrl, setSnapshortUrl] = useState<string | null>(null);
  const [backgroundImageLoaded, setBackgroundImageLoaded] = useState(false);
  const [isShareReady, setIsShareReady] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [waitingForCapture, setWaitingForCapture] = useState(false);
  const waitingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Preload the main shot image with higher priority
  const { preloadImages } = useImagePreloader([], {
    priority: true,
    maxConcurrent: 3,
  });

  useEffect(() => {
    if (!planDetails) getPlanDetails();
  }, []);

  useEffect(() => {
    // Only capture after image is loaded
    if (backgroundImageLoaded) {
      const captureTimer = setTimeout(() => {
        captureShort();
      }, 300);
      return () => clearTimeout(captureTimer);
    } else {
      setIsShareReady(false);
    }
  }, [data, backgroundImageLoaded]);

  const proceedWithShare = async (imageUrl: string) => {
    if (!imageUrl) return;
    setIsSharing(true);
    try {
      if (isZymedApp()) {
        sendMessageToParent("shareZymedShot", {
          title: `Hey! Check this out - ${data?.title}`,
          url: imageUrl,
        });
      } else {
        if (navigator.share) {
          try {
            await navigator.share({
              text: `Hey! Check this out - ${data?.title}\nFind more on Tatvacare!`,
              files: [
                new File(
                  [await (await fetch(imageUrl)).blob()],
                  "tatva-shot.png",
                  {
                    type: "image/png",
                  }
                ),
              ],
            });
          } catch (error) {
            if (error instanceof Error && error.name !== 'AbortError') {
              console.error("Error sharing short:", error);
            }
          }
        } else {
          alert("Sharing is not supported on this browser.");
        }
      }
    } finally {
      setIsSharing(false);
      setWaitingForCapture(false);
    }
  };

  useEffect(() => {
    if (waitingForCapture && isShareReady && snapshotUrl) {
      setWaitingForCapture(false);
      if (waitingIntervalRef.current) {
        clearInterval(waitingIntervalRef.current);
        waitingIntervalRef.current = null;
      }
      proceedWithShare(snapshotUrl);
    }
  }, [waitingForCapture, isShareReady, snapshotUrl]);

  // Track previous URL to avoid unnecessary reloads
  const previousUrlRef = useRef<string | null>(null);
  const imageLoaderRef = useRef<{ cleanup: () => void } | null>(null);

  // Preload the main shot image when data changes
  useEffect(() => {
    if (data?.url) {
      const urlChanged = previousUrlRef.current !== data.url;

      // Cleanup previous loader if URL changed
      if (urlChanged && imageLoaderRef.current) {
        imageLoaderRef.current.cleanup();
        imageLoaderRef.current = null;
      }

      // Reset capture-related state only when URL changes
      if (urlChanged) {
        if (waitingIntervalRef.current) {
          clearInterval(waitingIntervalRef.current);
          waitingIntervalRef.current = null;
        }
        setWaitingForCapture(false);
        setIsShareReady(false);
        setSnapshortUrl(null);
        previousUrlRef.current = data.url;
      }

      // Check if image is already loaded (from persistent cache or browser cache)
      const initializeImage = async () => {
        // First check persistent cache
        if (ImageCache.has(data.url)) {
          setBackgroundImageLoaded(true);
          return;
        }

        // Check browser cache and load
        const img = new Image();
        let isMounted = true;
        let handled = false;

        const cleanup = () => {
          isMounted = false;
          handled = true;
          img.onload = null;
          img.onerror = null;
        };

        img.onload = () => {
          if (isMounted && !handled) {
            handled = true;
            ImageCache.set(data.url);
            setBackgroundImageLoaded(true);
          }
        };

        img.onerror = () => {
          if (isMounted && !handled) {
            handled = true;
            console.error("Error loading image:", data.url);
            setBackgroundImageLoaded(false);
          }
        };

        // Set src to trigger load (browser will use cache if available)
        img.src = data.url;

        // Check if image is already complete (browser cache)
        // This prevents skeleton flash for cached images
        if (img.complete && img.naturalHeight > 0) {
          // Image is in browser cache, set loaded immediately
          ImageCache.set(data.url);
          setBackgroundImageLoaded(true);
          handled = true;
        } else {
          // Image not in cache, will load via onload handler
          // Set initial state to false only if not in cache
          if (urlChanged) {
            setBackgroundImageLoaded(false);
          }
        }

        // Store cleanup function
        imageLoaderRef.current = { cleanup };

        // Preload for future use
        preloadImages([data.url]);
      };

      // Only initialize if URL changed or image not loaded yet
      if (urlChanged || !backgroundImageLoaded) {
        initializeImage();
      }
    } else {
      // Cleanup if no URL
      if (imageLoaderRef.current) {
        imageLoaderRef.current.cleanup();
        imageLoaderRef.current = null;
      }
      setBackgroundImageLoaded(false);
      setIsShareReady(false);
      setSnapshortUrl(null);
      previousUrlRef.current = null;
    }

    return () => {
      // Cleanup on unmount
      if (imageLoaderRef.current) {
        imageLoaderRef.current.cleanup();
        imageLoaderRef.current = null;
      }
    };
  }, [data?.url, backgroundImageLoaded, preloadImages]);

  // Cleanup on unmount: clear any pending waiting interval
  useEffect(() => {
    return () => {
      if (waitingIntervalRef.current) {
        clearInterval(waitingIntervalRef.current);
        waitingIntervalRef.current = null;
      }
    };
  }, []);

  const isLiked = data?.id ? likedShots[data.id] : false;
  const likeCount = data?.id ? likeCounts[data.id] || 0 : 0;
  const isLikePending = data?.id ? pendingLikes[data.id] : false;
  const isBookmarked = data?.id ? bookmarkedShots[data.id] : false;

  const { userPreferences } = tatvaShots;
  const allSameSpecialityId = userPreferences?.every(
    (item) => item?.speciality_id === userPreferences?.[0]?.speciality_id
  );

  const handleBookmark = async () => {
    // Only the save direction is a taxonomy event; un-saving is not.
    if (!isBookmarked) {
      trackZymedEvent(ZYMED_EVENTS.SHOTS_SAVE_SHOT, { content_id: data?.id });
    }
    try {
      await updateBookmarkStatus(data?.id, !bookmarkedShots[data?.id]);
      if (!bookmarkedShots[data?.id]) {
        setShowBookmarkToast(data?.id);
        setTimeout(() => {
          setShowBookmarkToast(null);
        }, 3000);
      }
    } catch (error) {
      console.error("Failed to update bookmark status:", error);
    }
  };

  const handleLike = async () => {
    if (!isLiked) trackZymedEvent(ZYMED_EVENTS.SHOTS_LIKE_SHOT, { content_id: data?.id });
    if (!data?.id || isLikePending) return;

    try {
      await updateLikeStatus(data.id, !isLiked);
    } catch (error) {
      console.error("Failed to update like status:", error);
    }
  };

  const captureShort = async () => {
    if (!shortRef.current) {
      console.warn("Cannot capture: ref not available");
      setIsShareReady(false);
      setIsCapturing(false);
      return;
    }
    try {
      const canvas = await html2canvas(shortRef.current, {
        useCORS: true, // If the content has images from other domains
        scale: 2, // Improve resolution if needed
        logging: false,
        allowTaint: true,
      });
      if (canvas && canvas.width > 0 && canvas.height > 0) {
        const dataUrl = canvas.toDataURL("image/png");
        if (dataUrl && dataUrl.length > 0) {
          setSnapshortUrl(dataUrl);
          setIsShareReady(true);
        } else {
          throw new Error("Failed to generate data URL");
        }
      } else {
        throw new Error("Invalid canvas generated");
      }
    } catch (error) {
      console.error("Error capturing short:", error);
      setIsShareReady(false);
    } finally {
      setIsCapturing(false);
    }
  };

  const shareSnapshot = async () => {
    trackZymedEvent(ZYMED_EVENTS.SHOTS_SHARE_SHOT, {
      content_id: data?.id,
      // The share sheet is the OS one and never reports which app was picked.
      share_channel: "system_share_sheet",
    });
    // Prevent multiple simultaneous share requests
    if (isSharing || waitingForCapture) {
      return;
    }

    if (!isShareReady || !snapshotUrl) {
      setWaitingForCapture(true);
      let attempts = 0;
      const maxAttempts = 50;
      waitingIntervalRef.current = setInterval(() => {
        attempts++;
        if (attempts >= maxAttempts) {
          if (waitingIntervalRef.current) {
            clearInterval(waitingIntervalRef.current);
            waitingIntervalRef.current = null;
          }
          setWaitingForCapture(false);
          console.error("Snapshot capture timed out");
          alert("Unable to prepare image for sharing. Please try again.");
        }
      }, 200);
      return;
    }
    proceedWithShare(snapshotUrl);
  };

  const handleTag = (tag: any) => {
    navigate(".", { state: { tag, type: "shotTags" } });
  };

  return (
    <div
      className="flex flex-col w-full mx-auto"
      ref={shortRef}
      /*
       * Fills its snap slide, which is already exactly one viewport tall.
       *
       * This used to be `calc(var(--vh, 1vh) * 96)` while the container was 100 units and
       * the drag animated by window.innerHeight, so the three numbers had to be kept in
       * agreement by hand and any drift left the page scrollable. The slide now owns the
       * height and the shot simply fills it.
       */
      style={{ height: "100%" }}
    >
      {/* Image Section */}
      <div className="relative h-[40vh] max-h-[700px]:h-[30vh] sm:h-[35vh] md:h-[40vh] lg:h-[45vh] w-full overflow-hidden">
        {/* Background Image with Optimized Loading */}
        {!backgroundImageLoaded && (
          <div className="absolute inset-0 bg-gray-200 skeleton-pulse flex items-center justify-center">
            <svg
              className="w-16 h-16 text-gray-400"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        )}
        <div
          className={`absolute inset-0 bg-cover bg-center transition-opacity duration-300 ${backgroundImageLoaded ? "opacity-100" : "opacity-0"
            }`}
          style={{
            backgroundImage: backgroundImageLoaded
              ? `url(${data?.url})`
              : "none",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          {/* Like and Count */}
            <div className="absolute top-4 left-4 flex items-center space-x-3">
            {showTabsBar && <button
                className="w-8 h-8 rounded-full bg-white bg-opacity-60 flex items-center justify-center backdrop-blur-md"
                onClick={() => navigate(-1)}
            >
                <img
                    loading="lazy"
                    src={backIcon}
                    />
            </button> }
            </div>
            <div className="absolute bottom-4 left-4 flex items-center space-x-3">

              <button
              className="w-8 h-8 rounded-full bg-white bg-opacity-60 flex items-center justify-center backdrop-blur-md"
              onClick={handleLike}
            >
              {isLiked ? <BiSolidLike size={20} /> : <BiLike size={20} />}
            </button>
            <span className="text-white text-sm sm:text-base">
              {likeCount || ""}
            </span>
          </div>

          {/* Share and Bookmark */}
          <div className="absolute bottom-4 right-4 flex space-x-3">
            <button
              className={`w-8 h-8 rounded-full bg-white bg-opacity-60 flex items-center justify-center backdrop-blur-md transition-all ${
                isSharing || waitingForCapture ? 'opacity-60' : 'hover:scale-110 active:scale-95'
              }`}
              onClick={shareSnapshot}
              disabled={isSharing || waitingForCapture}
              title={
                waitingForCapture 
                  ? 'Preparing image for sharing...' 
                  : isSharing 
                    ? 'Sharing...' 
                    : 'Share'
              }
            >
              {isSharing || waitingForCapture ? (
                <Spin size="small" />
              ) : (
                <GoShareAndroid size={20} />
              )}
            </button>
            <button
              className="w-8 h-8 rounded-full bg-white bg-opacity-60 flex items-center justify-center backdrop-blur-md"
              onClick={handleBookmark}
            >
              {isBookmarked ? (
                <GoBookmarkFill size={20} />
              ) : (
                <BiBookmark size={20} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="flex flex-col flex-grow w-full p-4 text-left space-y-2">
        {/* Tag */}
        <div className="self-start px-2 py-1 text-xs bg-gray-100 text-yellow-900 rounded-3xl">
          {topic ||
            userPreferences?.find((pref) =>
              data?.speciality?.some((s: any) => s.id === pref.speciality_id)
            )?.code ||
            data?.speciality?.[0]?.code ||
            "General Medicine"}
        </div>

        {/* Tags */}
        <div className="text-indigo-700 text-xs sm:text-sm md:text-base lg:text-lg space-x-1 flex flex-wrap">
          {Array.from(
            data?.tags
              ?.reduce(
                (map: any, obj: any) => map.set(obj.tagName, obj),
                new Map()
              )
              .values()
          )?.map((tag: any, i: number) => (
            <span
              onClick={() => handleTag(tag)}
              key={i}
              className="cursor-pointer"
            >
              {tag?.tagName}
            </span>
          ))}
        </div>

        {/* Title */}
        <div className="mt-2 text-base sm:text-lg md:text-xl lg:text-2xl font-semibold">
          {data?.title}
        </div>

        {/* Description */}
        <div className="mt-1 text-sm sm:text-base md:text-lg font-light">
          <div dangerouslySetInnerHTML={{ __html: data?.description }} />
        </div>

        <div className="grow"></div>

        {/* Read More - Only show if readMoreUrl exists */}
        {data?.readMoreUrl && (
          <div className="mt-auto text-xs sm:text-sm md:text-base font-medium mb-4">
            <a
              href={data?.readMoreUrl}
              target="_blank"
              onClick={(e) => {
                trackZymedEvent(ZYMED_EVENTS.SHOTS_READ_MORE, { content_id: data?.id });
                if (isZymedApp()) {
                  e.preventDefault();
                  sendMessageToParent("openExternalLink", {
                    url: data?.readMoreUrl,
                  });
                }
              }}
              rel="noopener noreferrer"
              className="underline"
            >
              {data?.readMoreLable || "Read more"}
            </a>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-between items-center text-xs sm:text-sm text-gray-500">
          <span>
            {data?.createdAt &&
              new Date(data?.createdAt).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
          </span>
          {!isSelfShotsPlan && shouldShowSponsorship() && (medleyImage || service_payer?.containerName) && (
            <div className="flex items-center gap-2">
              <span>Sponsored By</span>
              <OptimizedImage
                src={medleyImage || `${service_payer?.containerName}`}
                alt="Service Provider"
                className="h-[25px] w-auto object-contain"
                height="25px"
                useIntersectionObserver={false}
                fallbackSrc={medleyImage}
              />
            </div>
          )}
        </div>
      </div>
      {showBookmarkToast === data?.id && <BookmarkToast />}
    </div>
  );
}